import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  output,
  signal,
  TemplateRef,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { EMPTY, Observable } from 'rxjs';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { DepartmentTreeItem, DepartmentTreeNode } from '../../department-model';
import { DepartmentRow, flattenDepartmentRows } from '../department-tree.utils';
import { AvatarPipe } from '../../../../../../../ui-components/pipes/avatar/avatar.pipe';
import { TruncatePipe } from '../../../../../../../ui-components/pipes/truncate/truncate.pipe';
import { TableColumns } from '../../../../../../../ui-components/lib/table/table.model';
import { FilteredTableComponent } from '../../../../../../common/components/filtered-table/filtered-table.component';
import {
  FilteredTablePayload,
  SortColumn,
} from '../../../../../../common/components/filtered-table/filtered-table.model';

@Component({
  selector: 'lib-department-tree-table',
  imports: [
    NgTemplateOutlet,
    TranslocoPipe,
    AvatarPipe,
    TruncatePipe,
    FilteredTableComponent,
  ],
  templateUrl: './department-tree-table.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DepartmentTreeTableComponent {
  readonly roots = input.required<DepartmentTreeNode[]>();
  readonly expandedIds = input.required<ReadonlySet<number>>();
  readonly expandAll = input(false);
  readonly loading = input(false);
  readonly searchTemplateRef = input<TemplateRef<unknown> | null>(null);
  readonly toolbarActionsTemplateRef = input<TemplateRef<unknown> | null>(null);
  readonly actionTemplate = input<TemplateRef<{
    $implicit: DepartmentTreeItem;
  }> | null>(null);

  readonly toggleExpanded = output<number>();
  readonly selectDepartment = output<DepartmentTreeItem>();

  private readonly transloco = inject(TranslocoService);

  protected readonly payload = signal<FilteredTablePayload>({
    page: 1,
    size: 50,
    search: '',
    filters: {},
    sortColumn: { key: '', value: 'asc' } satisfies SortColumn,
    tab: { key: 'all', identifier: 'all', label: 'All', active: true },
  });

  protected readonly applyOnStreamChanges = (): Observable<unknown> => EMPTY;

  protected readonly rows = computed(() =>
    flattenDepartmentRows(this.roots(), this.expandedIds(), this.expandAll())
  );

  protected readonly columns = computed<TableColumns[]>(() => [
    {
      header: this.transloco.translate(
        'department_list_screen_department_table_header'
      ),
    },
    { header: this.transloco.translate('department_tree_id_table_header') },
    { header: this.transloco.translate('department_tree_type_table_header') },
    { header: this.transloco.translate('department_tree_track_table_header') },
    {
      header: this.transloco.translate(
        'department_tree_manager_position_table_header'
      ),
    },
    {
      header: this.transloco.translate(
        'department_tree_positions_table_header'
      ),
    },
    { header: '' },
  ]);

  protected selectRow(row: unknown): void {
    this.selectDepartment.emit((row as DepartmentRow).department);
  }

  protected onToggleExpanded(id: string | number): void {
    this.toggleExpanded.emit(Number(id));
  }
}
